import { cancellationInformationForPurchaseQuery, purchaseQuery, useCancelPurchaseMutation } from "@/api/purchases";
import { PageInset } from "@/components/PageInset";
import { PurchaseDialog } from "@/components/PurchaseDialog";
import { TokenAmount } from "@/components/TokenAmount";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogClose } from "@/components/ui/Dialog";
import { IconButton } from "@/components/ui/IconButton";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { RECEIPT_DIALOG_EXIT_DURATION } from "@/components/ui/ReceiptDialog";
import { Textarea } from "@/components/ui/Textarea";
import { formatDate, formatTime } from "@/utils/date";
import { locale } from "@common/utils/i18n";
import { Box, Field, Flex, Img, QueryHandler, Separator, Text, UnstyledButton } from "@givenwell/components";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon } from "@givenwell/core-solid-icons";
import { GetPurchaseResponse, Policy } from "@givenwell/marketplace-api";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { Navigate, createFileRoute, getRouteApi, useNavigate, useRouter } from "@tanstack/react-router";
import { ReactNode, useState } from "react";

export const Route = createFileRoute("/_auth/_app/purchases/$purchaseId/cancel")({
  component: Page,
});

const route = getRouteApi("/_auth/_app/purchases/$purchaseId/cancel");

function Page() {
  const { purchaseId } = route.useParams();
  const purchaseDetailsQuery = useQuery(purchaseQuery(purchaseId));
  const infoQuery = useQuery(cancellationInformationForPurchaseQuery(purchaseId));

  const [step, setStep] = useState<"confirm" | "start" | "reason">("start");
  const [reason, setReason] = useState("");

  return (
    <PageWrapper css={{ pb: 0 }}>
      <QueryHandler
        query={purchaseDetailsQuery}
        error={() => <Navigate to="/purchases" replace />}
        success={({ data: purchase }) => {
          return (
            <QueryHandler
              query={infoQuery}
              error={() => <Navigate to="/purchases" replace />}
              success={({ data: info }) => {
                if (!info.current_policy) {
                  return <Navigate to="/purchases" replace />;
                }
                if (step === "start") {
                  return (
                    <StartCancelStep
                      purchase={purchase}
                      onContinue={reason => {
                        setReason(reason);
                        if (reason) {
                          setStep("confirm");
                        } else {
                          setStep("reason");
                        }
                      }}
                    />
                  );
                }
                if (step === "reason") {
                  return (
                    <ReasonStep
                      onBack={() => setStep("start")}
                      onContinue={reason => {
                        setReason(reason);
                        setStep("confirm");
                      }}
                    />
                  );
                }
                if (step === "confirm") {
                  return (
                    <ConfirmCancelStep
                      purchase={purchase}
                      reason={reason}
                      currentPolicy={info.current_policy}
                      policies={info.all_policies}
                      onBack={() => setStep("start")}
                    />
                  );
                }
                return (
                  <StartCancelStep
                    purchase={purchase}
                    onContinue={reason => {
                      setReason(reason);
                      if (reason) {
                        setStep("confirm");
                      } else {
                        setStep("reason");
                      }
                    }}
                  />
                );
              }}
            />
          );
        }}
      />
    </PageWrapper>
  );
}

const REASONS = [
  { label: "My schedule has changed", value: "My schedule has changed" },
  { label: "I’ve changed my mind", value: "I’ve changed my mind" },
  {
    label: "Supplier has asked me to cancel",
    value: "Supplier has asked me to cancel",
  },
  { label: "Other reason", value: "" },
];

function StartCancelStep({
  purchase,
  onContinue,
}: {
  purchase: GetPurchaseResponse;
  onContinue: (reason: string) => void;
}) {
  const router = useRouter();

  if (purchase.purchase_status !== "upcoming") {
    return <div>Too bad bucko</div>;
  }

  return (
    <>
      <Flex css={{ h: 64, items: "center", px: 8 }}>
        <IconButton aria-label="Go back" variant="plain" onClick={() => router.history.back()}>
          <ChevronLeftIcon />
        </IconButton>
        <Box css={{ flex: 1 }} />
        <Text css={{ weight: 500 }}>Cancel booking</Text>
        <Box css={{ flex: 1 }} />
        <IconButton aria-label="Close" variant="plain" onClick={() => router.history.back()}>
          <CloseIcon />
        </IconButton>
      </Flex>
      <PageInset css={{ gap: 16, flexGrow: 1, pb: 16 }}>
        <Text css={{ fontScale: "2xl", fontWeight: 700 }}>Reason for cancelling</Text>
        <div>
          {REASONS.map(reason => (
            <UnstyledButton
              key={reason.value}
              css={{
                w: "100%",
                h: 60,
                d: "flex",
                items: "center",
                justify: "space-between",
                borderTop: "1px solid",
                borderColor: colors.outline,
                color: colors.blue500,
              }}
              onClick={() => onContinue(reason.value)}
            >
              {reason.label}
              <ChevronRightIcon />
            </UnstyledButton>
          ))}
        </div>
      </PageInset>
    </>
  );
}

function ReasonStep({ onBack, onContinue }: { onBack: () => void; onContinue: (reason: string) => void }) {
  const router = useRouter();
  const [reason, setReason] = useState("");
  return (
    <>
      <Flex css={{ h: 64, items: "center", px: 8 }}>
        <IconButton aria-label="Go back" variant="plain" onClick={onBack}>
          <ChevronLeftIcon />
        </IconButton>
        <Box css={{ flex: 1 }} />
        <Text css={{ weight: 500 }}>Cancel booking</Text>
        <Box css={{ flex: 1 }} />
        <IconButton aria-label="Close" variant="plain" onClick={() => router.history.back()}>
          <CloseIcon />
        </IconButton>
      </Flex>
      <PageInset css={{ gap: 16, flexGrow: 1, pb: 16 }}>
        <Text css={{ fontScale: "2xl", fontWeight: 700 }}>Please tell us more</Text>
        <Text>
          Please tell us why you’ve decided to cancel this booking. Your feedback won’t be shared with the supplier.
        </Text>
        <form
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
          onSubmit={e => {
            e.preventDefault();
            onContinue(reason);
          }}
        >
          <Field>
            <Textarea placeholder="Your feedback helps us improve" value={reason} onValueChange={setReason} />
          </Field>
          <Box css={{ flex: 1 }} />
          <Button type="submit" variant="tonal" color="primary" size="lg" disabled={!reason}>
            Continue
          </Button>
        </form>
      </PageInset>
    </>
  );
}

function ConfirmCancelStep({
  purchase,
  currentPolicy,
  policies,
  onBack,
  reason,
}: {
  purchase: GetPurchaseResponse;
  currentPolicy: Policy;
  policies: Policy[];
  reason: string;
  onBack: () => void;
}) {
  const router = useRouter();
  const mut = useCancelPurchaseMutation(purchase.purchase_id);
  const navigate = useNavigate();
  const [completed, setCompleted] = useState(false);
  return (
    <>
      <Flex css={{ h: 64, items: "center", px: 8 }}>
        <IconButton aria-label="Go back" variant="plain" onClick={onBack}>
          <ChevronLeftIcon />
        </IconButton>
        <Box css={{ flex: 1 }} />
        <Text css={{ weight: 500 }}>Cancel booking</Text>
        <Box css={{ flex: 1 }} />
        <IconButton aria-label="Close" variant="plain" onClick={() => router.history.back()}>
          <CloseIcon />
        </IconButton>
      </Flex>
      <PageInset css={{ gap: 16, flexGrow: 1, pb: 16 }}>
        <Text css={{ fontScale: "2xl", fontWeight: 700 }}>Confirm cancellation</Text>
        <PurchaseTile purchase={purchase} />
        <PolicyBox policy={currentPolicy} policies={policies} />
        {!purchase.eap_used && (
          <>
            <Separator />
            <RefundDetails policy={currentPolicy} purchase={purchase} />
          </>
        )}
        <Box css={{ flex: "1 0 0px" }} />
        <Button
          variant="filled"
          color="red"
          loading={mut.isPending}
          onClick={() => {
            if (!currentPolicy) return;
            mut.mutate(
              {
                expected_refund_policy_entry_id: currentPolicy?.policy_entry_id,
                reason,
              },
              {
                onSuccess() {
                  setCompleted(true);
                },
              },
            );
          }}
        >
          Cancel booking
        </Button>
      </PageInset>
      <PurchaseDialog
        purchaseId={purchase.purchase_id}
        open={completed}
        onOpenChange={open => {
          setCompleted(open);
          if (!open) {
            setTimeout(() => {
              navigate({ to: "/purchases" });
            }, RECEIPT_DIALOG_EXIT_DURATION);
          }
        }}
      />
    </>
  );
}

function PurchaseTile({ purchase }: { purchase: GetPurchaseResponse }) {
  return (
    <Flex
      css={{
        p: 10,
        border: `1px solid ${colors.outline}`,
        pos: "relative",
        rounded: 8,
        gap: 8,
      }}
    >
      <Box
        css={{
          w: 135,
          pos: "relative",
          aspectRatio: purchase.eap_used ? "1" : "16/9",
          overflow: "hidden",
          rounded: 4,
          cursor: "pointer",
        }}
      >
        <Img
          css={{
            pos: "absolute",
            top: 0,
            left: 0,
            w: "100%",
            h: "100%",
            objectFit: "cover",
          }}
          src={purchase.media_url ?? undefined}
        />
      </Box>
      <Flex css={{ flex: 1, flexDir: "column", justify: "space-between" }}>
        <div>
          <Text css={{ lineClamp: 2, fontScale: "sm", weight: 700 }}>{purchase.listing_title}</Text>
          <Text css={{ fontScale: "xs", weight: 500, mt: 4 }}>{purchase.variant_title}</Text>
        </div>
        <div>
          {purchase.booking?.starts_at && (
            <Flex css={{ gap: 4 }}>
              <CalendarIcon />
              <Text css={{ fontScale: "2xs", lineHeight: "12px" }}>
                {formatTime(new Date(purchase.booking.starts_at))}
                <br />
                {formatDate(new Date(purchase.booking.starts_at))}
              </Text>
            </Flex>
          )}
        </div>
      </Flex>
    </Flex>
  );
}

function PolicyBox({ policy, policies }: { policy: Policy; policies: Policy[] }) {
  const refundPolicyText =
    policy.refund_percentage === 100
      ? "Upon cancellation you will receive a full refund as you’re inside the full refund window"
      : policy.refund_percentage === 0
        ? "Upon cancellation, please note that no refund will be issued as our policy does not allow for refunds under these circumstances"
        : "Upon cancellation you will receive a partial refund as you’re inside the partial refund window";
  return (
    <Box css={{ spaceY: 8 }}>
      <Flex css={{ justify: "space-between" }}>
        <Text css={{ fontWeight: 700, mb: 4 }}>Refund policy</Text>
        <PolicyDetailsDialog policies={policies}>
          <Button variant="plain" color="primary" size="xs">
            View details
          </Button>
        </PolicyDetailsDialog>
      </Flex>
      <Text>{refundPolicyText}</Text>
    </Box>
  );
}

function RefundDetails({ policy, purchase }: { policy: Policy; purchase: GetPurchaseResponse }) {
  return (
    <Box css={{ spaceY: 16 }}>
      <Box>
        <Text>Purchase price</Text>
        <TokenAmount amount={purchase.token_amount} size="2xl" showIcon showFreeForZero={false} />
      </Box>
      <Box>
        <Text>Refund amount</Text>
        <TokenAmount amount={policy.refunded_amount} size="2xl" showIcon showFreeForZero={false} />
      </Box>
    </Box>
  );
}

function PolicyDetailsDialog({ children, policies }: { children?: ReactNode; policies: Policy[] }) {
  return (
    <Dialog
      title="Cancellation policy"
      content={
        <>
          {policies.length === 1 && <Text css={{ fontScale: "lg" }}>{getPolicyText(policies[0]!)}</Text>}
          {policies.length > 1 && (
            <>
              <Text css={{ fontScale: "lg" }}>Cancel by</Text>
              <dl
                style={{
                  display: "table",
                  width: "100%",
                }}
              >
                {policies.map(policy => (
                  <div
                    key={policy.policy_entry_id}
                    style={{
                      display: "table-row",
                    }}
                  >
                    <dt
                      style={{
                        display: "table-cell",
                        borderBottom: `1px solid ${colors.outline}`,
                        padding: "8px 0",
                        width: 100,
                      }}
                    >
                      <Text css={{ weight: 700 }}>{policyDateFormatter.format(new Date(policy.valid_until))}</Text>
                      <Text>{policyTimeFormatter.format(new Date(policy.valid_until))}</Text>
                    </dt>
                    <dd
                      style={{
                        display: "table-cell",
                        borderBottom: `1px solid ${colors.outline}`,
                      }}
                    >
                      {getPolicyText(policy)}
                    </dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </>
      }
      footer={
        <DialogClose>
          <Button variant="plain" color="primary">
            OK
          </Button>
        </DialogClose>
      }
    >
      {children}
    </Dialog>
  );
}

function getPolicyText(policy: Policy) {
  switch (policy.refund_percentage) {
    case 0:
      return "No refund";
    case 100:
      return "Full refund: get back 100% of what you paid";
    default:
      return `Partial refund: Get back ${policy.refund_percentage}% of what you paid`;
  }
}

const policyDateFormatter = new Intl.DateTimeFormat(locale, {
  month: "short",
  day: "numeric",
});

const policyTimeFormatter = new Intl.DateTimeFormat(locale, {
  hour: "numeric",
  minute: "numeric",
});
